/**
 * IPL card markup.
 *
 * Pure string builders — they read their arguments and nothing else, so they
 * can be changed without touching the controller that inserts them or the
 * validation that walks the resulting cards. The ids and field classes they
 * emit are part of the contract main.js depends on.
 */

/** One "yesterday's result" card. */
export function resultCardHtml({ resultId, resultIndex, data, teams }) {
  const IPL_TEAMS = teams;
  return `
    <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
      <h3 class="text-lg font-bold text-gray-700 flex items-center gap-2">
        <span class="bg-brand-accent text-white px-3 py-1 rounded-lg text-sm">Result ${resultIndex}</span>
        <span class="text-sm text-gray-500 font-normal">Yesterday's Match</span>
      </h3>
      <button type="button" onclick="removeIplMatchResult('${resultId}')" class="text-gray-400 hover:text-red-600 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      </button>
    </div>
    <div class="space-y-4 mb-6">
      <!-- Basic Match Information -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="label">Match Legend</label>
          <input type="text" class="input text-base ipl-res-matchLegend bg-white" placeholder="e.g. Match 23" value="${data?.matchLegend || ''}">
        </div>
        <div>
          <label class="label">Venue</label>
          <input type="text" class="input text-base ipl-res-venue bg-white" placeholder="e.g. Wankhede Stadium, Mumbai" value="${data?.venue || ''}">
        </div>
      </div>
      
      <!-- Result Type Selection -->
      <div>
        <label class="label">Match Result Type</label>
        <select class="input text-base ipl-res-resultType bg-white" onchange="updateResultFieldsVisibility('${resultId}')">
          <option value="normal" ${(!data?.resultType || data?.resultType === 'normal') ? 'selected' : ''}>Normal Result</option>
          <option value="no-result" ${data?.resultType === 'no-result' ? 'selected' : ''}>🚫 No Result (Rain/Washout)</option>
          <option value="tie" ${data?.resultType === 'tie' ? 'selected' : ''}>🤝 Tie</option>
          <option value="super-over" ${data?.resultType === 'super-over' ? 'selected' : ''}>⚡ Super Over Result</option>
          <option value="abandoned" ${data?.resultType === 'abandoned' ? 'selected' : ''}>⛔ Abandoned</option>
          <option value="forfeit" ${data?.resultType === 'forfeit' ? 'selected' : ''}>🏳️ Forfeit</option>
        </select>
      </div>
    </div>
    
    <!-- Team Selection (only shown for result types that need it) -->
    <div id="team-selection-fields-${resultId}" class="${(!data?.resultType || data?.resultType === 'normal' || data?.resultType === 'super-over') ? 'hidden' : ''} space-y-4 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="label" id="team1-label-${resultId}">👕 Team 1</label>
          <select class="input text-base ipl-team-select ipl-res-team1 bg-white" onchange="updateTeamLabels('${resultId}')">
            <option value="">Select Team</option>
            ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.team1 === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
          </select>
        </div>
        <div>
          <label class="label" id="team2-label-${resultId}">👕 Team 2</label>
          <select class="input text-base ipl-team-select ipl-res-team2 bg-white" onchange="updateTeamLabels('${resultId}')">
            <option value="">Select Team</option>
            ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.team2 === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
          </select>
        </div>
      </div>
    </div>
    
    <!-- Normal Result Fields -->
    <div id="normal-result-fields-${resultId}" class="${data?.resultType && data?.resultType !== 'normal' ? 'hidden' : ''}">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label class="label">Winning Team</label>
          <select class="input text-base ipl-team-select ipl-res-winner bg-white">
            <option value="">Select Winner</option>
            ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.winner === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
          </select>
        </div>
        <div>
          <label class="label">Losing Team</label>
          <select class="input text-base ipl-team-select ipl-res-loser bg-white">
            <option value="">Select Loser</option>
            ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.loser === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
          </select>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label class="label">Margin</label>
          <div class="flex gap-2">
            <select class="input text-base ipl-res-marginType w-1/2 bg-white">
              <option value="Runs" ${data?.marginType === 'Runs' ? 'selected' : ''}>Runs</option>
              <option value="Wickets" ${data?.marginType === 'Wickets' ? 'selected' : ''}>Wickets</option>
              <option value="Super Over" ${data?.marginType === 'Super Over' ? 'selected' : ''}>Super Over</option>
            </select>
            <input type="text" class="input text-base ipl-res-marginValue w-1/2 bg-white" placeholder="Value" value="${data?.marginValue || ''}">
          </div>
        </div>
      </div>
    </div>
    
    <!-- No Result Fields -->
    <div id="no-result-fields-${resultId}" class="${data?.resultType === 'no-result' ? '' : 'hidden'} mb-4">
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/>
          </svg>
          <h4 class="font-semibold text-amber-800">No Result Details</h4>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-amber-700 mb-1">🌧️ Reason</label>
            <select class="input ipl-res-noResultReason bg-white">
              <option value="">Select Reason</option>
              <option value="rain" ${data?.noResultReason === 'rain' ? 'selected' : ''}>Rain</option>
              <option value="wet-outfield" ${data?.noResultReason === 'wet-outfield' ? 'selected' : ''}>Wet Outfield</option>
              <option value="bad-light" ${data?.noResultReason === 'bad-light' ? 'selected' : ''}>Bad Light</option>
              <option value="unplayable-pitch" ${data?.noResultReason === 'unplayable-pitch' ? 'selected' : ''}>Unplayable Pitch</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-amber-700 mb-1">📝 Notes</label>
            <input type="text" class="input ipl-res-noResultNotes bg-white" placeholder="e.g. Match abandoned after 12.3 overs" value="${data?.noResultNotes || ''}">
          </div>
        </div>
      </div>
    </div>
    
    <!-- Tie Result Fields -->
    <div id="tie-result-fields-${resultId}" class="${data?.resultType === 'tie' ? '' : 'hidden'} mb-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h4 class="font-semibold text-blue-800">Tie Match Details</h4>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="label"><span id="tie-team1-label-${resultId}">Team 1</span> Score</label>
            <input type="text" class="input ipl-res-team1Score bg-white" placeholder="e.g. 180/8" value="${data?.team1Score || ''}">
          </div>
          <div>
            <label class="label"><span id="tie-team2-label-${resultId}">Team 2</span> Score</label>
            <input type="text" class="input ipl-res-team2Score bg-white" placeholder="e.g. 180/9" value="${data?.team2Score || ''}">
          </div>
        </div>
      </div>
    </div>
    
    <!-- Super Over Result Fields -->
    <div id="super-over-result-fields-${resultId}" class="${data?.resultType === 'super-over' ? '' : 'hidden'} mb-4">
      <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
          <h4 class="font-semibold text-purple-800">Super Over Details</h4>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="label">Super Over Winner</label>
            <select class="input ipl-team-select ipl-res-superOverWinner bg-white">
              <option value="">Select Winner</option>
              ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.superOverWinner === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
            </select>
          </div>
          <div>
            <label class="label">Super Over Loser</label>
            <select class="input ipl-team-select ipl-res-superOverLoser bg-white">
              <option value="">Select Loser</option>
              ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.superOverLoser === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
            </select>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label class="label">Super Over Margin</label>
            <input type="text" class="input ipl-res-superOverMargin bg-white" placeholder="e.g. 2 wickets" value="${data?.superOverMargin || ''}">
          </div>
          <div>
            <label class="label"><span id="super-team1-label-${resultId}">Team 1</span> Super Over Score</label>
            <input type="text" class="input ipl-res-team1SuperOverScore bg-white" placeholder="e.g. 15/1" value="${data?.team1SuperOverScore || ''}">
          </div>
        </div>
      </div>
    </div>
    
    <!-- Abandoned/Forfeit Fields -->
    <div id="abandoned-result-fields-${resultId}" class="${(data?.resultType === 'abandoned' || data?.resultType === 'forfeit') ? '' : 'hidden'} mb-4">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          <h4 class="font-semibold text-red-800">${data?.resultType === 'forfeit' ? 'Forfeit' : 'Abandoned'} Details</h4>
        </div>
        <div class="grid grid-cols-1 gap-3">
          <div>
            <label class="block text-xs font-medium text-red-700 mb-1">📝 Reason</label>
            <textarea class="input ipl-res-abandonReason bg-white" rows="2" placeholder="Detailed reason for abandonment/forfeit">${data?.abandonReason || ''}</textarea>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Man of the Match Section (hidden for no-result, abandoned, forfeit) -->
    <div id="mom-section-${resultId}" class="${(['no-result', 'abandoned', 'forfeit'].includes(data?.resultType)) ? 'hidden' : ''} mt-6 pt-5 border-t border-gray-200">
      <div class="flex items-center gap-2 mb-4">
        <div class="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
          </svg>
        </div>
        <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wider">Man of the Match</h3>
      </div>
      
      <div class="bg-white p-4 rounded-xl border border-gray-200">
        <div class="space-y-4">
          <!-- Name & Role Row -->
          <div class="max-w-md">
            <label class="u-overline">Player Name & Role</label>
            <div class="flex gap-2">
              <input type="text" class="input ipl-res-mom w-2/3 bg-gray-50 border-gray-200 focus:bg-white transition-all font-semibold" placeholder="e.g. Rajat Patidar" value="${data?.manOfTheMatch || ''}">
              <select class="input ipl-res-momType w-1/3 bg-gray-50 border-gray-200 focus:bg-white transition-all" onchange="updateMomStatsVisibility('${resultId}')">
                <option value="Batter" ${data?.momType === 'Batter' ? 'selected' : ''}>Batter</option>
                <option value="Bowler" ${data?.momType === 'Bowler' ? 'selected' : ''}>Bowler</option>
                <option value="All-rounder" ${(data?.momType === 'All-rounder' || !data?.momType) ? 'selected' : ''}>All-rounder</option>
              </select>
            </div>
          </div>
          
          <!-- Stats Row -->
          <div class="flex flex-wrap items-center gap-6">
            <!-- Batting Mini-Card -->
            <div class="stats-batting flex items-center gap-3 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <span class="u-overline">Batting:</span>
              <div class="flex gap-2">
                <div>
                  <input type="text" class="input w-16 bg-white border-gray-200 ipl-res-momRuns" placeholder="Runs" value="${data?.momStats?.runs || ''}">
                </div>
                <div>
                  <input type="number" class="input w-16 bg-white border-gray-200 ipl-res-momBalls" placeholder="Balls" value="${data?.momStats?.balls || ''}">
                </div>
              </div>
            </div>
            
            <!-- Bowling Mini-Card -->
            <div class="stats-bowling flex items-center gap-3 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <span class="u-overline">Bowling:</span>
              <div class="flex gap-2">
                <div>
                  <input type="number" class="input w-16 bg-white border-gray-200 ipl-res-momWickets" placeholder="Wkts" value="${data?.momStats?.wickets || ''}">
                </div>
                <div>
                  <input type="number" class="input w-16 bg-white border-gray-200 ipl-res-momRunsConceded" placeholder="Runs" value="${data?.momStats?.runsConceded || ''}">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/** One "today's fixture" card. */
export function fixtureCardHtml({ fixtureId, fixtureIndex, data, teams }) {
  const IPL_TEAMS = teams;
  return `
    <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
      <h3 class="text-base font-bold text-gray-700 flex items-center gap-2">
        <span class="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm">Fixture ${fixtureIndex}</span>
        <span class="text-sm text-gray-500 font-normal">Today's Match</span>
      </h3>
      <button type="button" onclick="removeIplFixture('${fixtureId}')" class="text-gray-400 hover:text-red-600 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      </button>
    </div>
    <div class="space-y-4">
      <!-- First Row: Match Legend and Teams -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="label">Match Legend</label>
          <input type="text" class="input text-base fixture-matchLegend bg-white" placeholder="e.g. Match 24" value="${data?.matchLegend || ''}">
        </div>
        <div>
          <label class="label">👕 Team 1</label>
          <select class="input text-base ipl-team-select fixture-team1">
            <option value="">Select Team</option>
            ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.team1 === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
          </select>
        </div>
        <div>
          <label class="label">👕 Team 2</label>
          <select class="input text-base ipl-team-select fixture-team2">
            <option value="">Select Team</option>
            ${IPL_TEAMS.map(t => `<option value="${t.code}" ${data?.team2 === t.code ? 'selected' : ''}>${t.name} (${t.code})</option>`).join('')}
          </select>
        </div>
      </div>
      
      <!-- Second Row: Time and Venue -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="label">⏰ Start Time</label>
          <input type="text" class="input text-base fixture-time" placeholder="e.g. 7:30 PM IST" value="${data?.startTime || '7:30 PM IST'}">
        </div>
        <div>
          <label class="label">Venue</label>
          <input type="text" class="input text-base fixture-venue" placeholder="e.g. M. Chinnaswamy Stadium, Bengaluru" value="${data?.venue || ''}">
        </div>
      </div>
    </div>
  `;
}
